import React from 'react';
import { Clock, CheckCircle, UserCheck, Mail } from 'lucide-react';
import Icon from '../../../components/AppIcon';

const InvitationTracker = ({ invitations }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'converted':
        return {
          icon: 'CheckCircle',
          color: 'text-success',
          bgColor: 'bg-success/10',
          label: 'Converted',
          description: 'Upgraded to Premium'
        };
      case 'accepted':
        return {
          icon: 'UserCheck',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          label: 'Accepted',
          description: 'Signed up'
        };
      case 'pending':
        return {
          icon: 'Clock',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          label: 'Pending',
          description: 'Invitation sent'
        };
      default:
        return {
          icon: 'Mail',
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          label: 'Unknown',
          description: 'Status unknown'
        };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-therapeutic-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="Users" size={24} className="text-success" />
        </div>
        <div>
          <h2 className="text-lg font-heading font-bold text-foreground">Invitation Tracker</h2>
          <p className="text-sm text-muted-foreground">{invitations?.length} invitations sent</p>
        </div>
      </div>

      {/* Invitations List */}
      <div className="space-y-3">
        {invitations?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Mail" size={48} className="text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No invitations sent yet</p>
            <p className="text-xs text-muted-foreground mt-1">Share your referral link to get started</p>
          </div>
        ) : (
          invitations?.map((invitation) => {
            const statusConfig = getStatusConfig(invitation?.status);
            return (
              <div
                key={invitation?.id}
                className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary/30 transition-colors"
              >
                <div className={`flex-shrink-0 w-10 h-10 ${statusConfig?.bgColor} rounded-lg flex items-center justify-center`}>
                  <Icon name={statusConfig?.icon} size={20} className={statusConfig?.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-foreground truncate">
                      {invitation?.email}
                    </p>
                    <span className={`text-xs font-semibold ${statusConfig?.color} whitespace-nowrap`}>
                      {statusConfig?.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {statusConfig?.description}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>Sent: {formatDate(invitation?.sentAt)}</span>
                    {invitation?.convertedAt && (
                      <span className="text-success font-semibold">
                        Earned: £{invitation?.rewardEarned}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Timeline Progress */}
      {invitations?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Conversion Rate</span>
            <span className="font-semibold text-foreground">
              {Math.round((invitations?.filter(i => i?.status === 'converted')?.length / invitations?.length) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-success rounded-full h-2 transition-all"
              style={{
                width: `${(invitations?.filter(i => i?.status === 'converted')?.length / invitations?.length) * 100}%`
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationTracker;